import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  ViewChild,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { Popover } from 'primeng/popover';
import { Ag1ButtonComponent } from '../ag1-button/ag1-button.component';
import { Ag1LoaderComponent } from '../ag1-loader/ag1-loader.component';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Bind } from 'primeng/bind';
import { NgClass } from '@angular/common';
import { HumanizePipe } from '@app/core/pipes/humanize.pipe';

export interface ItemType {
  label: string;
  value?: string;
  group?: string;
}

@Component({
  selector: 'ag1-dropdown-button',
  templateUrl: './ag1-dropdown-button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Ag1ButtonComponent,
    Ag1LoaderComponent,
    AngularSvgIconModule,
    Bind,
    Popover,
    NgClass,
    HumanizePipe,
  ],
})
export class Ag1DropdownButtonComponent implements OnInit, OnChanges {
  private cdr = inject(ChangeDetectorRef);

  // Functionality to still add:
  // Allow user to navigate with keyboard (tabs and key presses) to select an item
  // Add aria-labels to dropdown and items
  // Add Humanize Pipe to Labels

  @Input()
  variant: 'primary' | 'secondary' | 'tertiary' = 'primary';

  @Input()
  disabled: boolean;

  @Input()
  isLoading: boolean = false;

  @Input()
  label: string = 'Download';

  @Input()
  items: ItemType[] = [];

  groupedItems: { [key: string]: ItemType[] } = {};

  isOpen: boolean = false;

  @Output()
  clickEvent = new EventEmitter<ItemType>();

  @ViewChild('dropdownPopover')
  dropdownPopover!: Popover;

  constructor() {}

  onDropdownTriggerClick(event: Event): void {
    if (this.disabled || this.items.length === 0) {
      return;
    }
    this.dropdownPopover.toggle(event);
  }

  ngOnInit(): void {
    if (!this.items.length) {
      return;
    }

    this.sortItems();
  }

  ngOnChanges(changes: SimpleChanges): void {
    for (const propName in changes) {
      if (Object.prototype.hasOwnProperty.call(changes, propName)) {
        switch (propName) {
          case 'items':
            this.sortItems();
            if (changes.items.currentValue?.length) {
              this.items = changes.items.currentValue;
              this.sortItems();
            }
            break;
        }
      }
    }
  }

  refreshInterface(): void {
    this.cdr.detectChanges();
  }

  sortItems(): void {
    if (!this.items.length) {
      return;
    }

    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    this.items = this.items.sort(
      (a: ItemType, b: ItemType) =>
        collator.compare(a?.group, b?.group) ||
        collator.compare(a.label, b.label),
    );

    if (!this.isGrouped) {
      return;
    }

    this.groupedItems = this.items.reduce((obj, item) => {
      // if for some reason the item doesn't have a group, add it to 'Other' group
      if (!item?.group) {
        const fallbackGroup = 'Other';
        if (!obj[fallbackGroup]) {
          // Initialize and add item in one go
          obj[fallbackGroup] = [item];
        } else {
          // The group exists, so just push the item
          obj[fallbackGroup].push(item);
        }
        return obj;
      }

      const groupKey: string = item.group;
      if (!obj[groupKey]) {
        // Initialize and add item in one go
        obj[groupKey] = [item];
      } else {
        // The group exists, so just push the item
        obj[groupKey].push(item);
      }

      return obj;
    }, {});
  }

  get isGrouped(): boolean {
    return this.items.some((item) =>
      Object.prototype.hasOwnProperty.call(item, 'group'),
    );
  }

  get groupKeys(): string[] {
    return Object.keys(this.groupedItems);
  }

  onClick(selectedItem: ItemType): void {
    if (!selectedItem) {
      return;
    }

    this.dropdownPopover.hide();
    this.clickEvent.emit(selectedItem);
  }
}
